// RepVantage native bridge — drop into the wedge web app (e.g. src/native/nativeBridge.ts)
// and import where the paywall + auth live. Keeps all web logic unchanged on the web;
// only changes behavior when running inside the iOS shell.
import { Capacitor } from '@capacitor/core';

export const isNativeIOS = (): boolean =>
  Capacitor.getPlatform() === 'ios' && Capacitor.isNativePlatform();

// ---- In-App Purchase (RevenueCat, handles StoreKit + server-side receipt validation) ----
// Product IDs must match App Store Connect exactly (subscription group "RepVantage Tiers").
// 2026-08-25 restructure: pitch_v2/close_v2 are DEDICATED new products (monthly
// only) so a NEW iOS subscriber gets the new caps (20/50) at $29.99/$59.99, while
// grandfathered iOS subs on the legacy pitch/close products keep 35/70. The
// legacy prep/pitch/close/dominate products stay for grandfathered subs.
// The two new products must be created + approved in App Store Connect before
// the iOS ladder goes live (gates the iOS launch).
export const IAP_PRODUCTS = {
  prep:     { monthly: 'io.repvantage.wedge.prep.monthly',     annual: 'io.repvantage.wedge.prep.annual' },
  pitch:    { monthly: 'io.repvantage.wedge.pitch.monthly',    annual: 'io.repvantage.wedge.pitch.annual' },
  close:    { monthly: 'io.repvantage.wedge.close.monthly',    annual: 'io.repvantage.wedge.close.annual' },
  dominate: { monthly: 'io.repvantage.wedge.dominate.monthly', annual: 'io.repvantage.wedge.dominate.annual' },
  // No annual SKU exists for the v2 tiers (monthly only); an annual request
  // must fail loudly rather than silently raise the monthly sheet.
  pitch_v2: { monthly: 'io.repvantage.wedge.pitch_v2.monthly', annual: undefined },
  close_v2: { monthly: 'io.repvantage.wedge.close_v2.monthly', annual: undefined },
} as const;

export async function initIAP(appUserId?: string): Promise<void> {
  if (!isNativeIOS()) return;
  /* 2026-07-27 · NEVER configure anonymously. An anonymous $RCAnonymousID
     purchase is unattributable — the RevenueCat webhook explicitly bails on
     anonymous app_user_ids, there is no logIn/reconcile path, so the rep is
     charged and the tier grant is lost forever. Callers poll until the
     signed-in user id exists; until then IAP simply isn't configured and a
     purchase attempt fails LOUDLY (better than silently mis-attributing). */
  if (!appUserId) return;
  const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
  await Purchases.setLogLevel({ level: LOG_LEVEL.WARN });
  // Public SDK key from the RevenueCat dashboard (safe to ship; NOT a secret).
  await Purchases.configure({ apiKey: 'appl_FrEsufiNStqazSdtPGldaPOnvnn', appUserID: appUserId });
}

/* Discriminated purchase result (2026-07-27). The old boolean collapsed every
   failure mode into the user-cancel toast ("Checkout canceled · no charge") —
   including REAL CHARGES whose entitlement sync lagged, which is the one
   message you must never show a rep who was just billed.
     entitled  · transaction done AND the entitlement is already active
     pending   · transaction done, entitlement not visible yet (webhook/sync
                 lag) — treat as success and let the tier poll confirm
     cancelled · the rep backed out of the Apple sheet · no charge
     failed    · anything else (not configured, product missing, StoreKit
                 error, already-owned with stale server tier, …) */
export type PurchaseOutcome = 'entitled' | 'pending' | 'cancelled' | 'failed';
export async function purchase(productId: string): Promise<PurchaseOutcome> {
  try {
    const { Purchases, PRODUCT_CATEGORY } = await import('@revenuecat/purchases-capacitor');
    /* 2026-08-26 · Bug 7/8 fix (the repeat App Review 2.1 reject) combined with the
       2026-07-29 frozen-bridge guard. Subscriptions used to resolve ONLY through
       getOfferings(); an empty or misconfigured RevenueCat Offering left Subscribe
       dead (no Apple sheet, no actionable error) while the pull packs (direct
       getProducts) always worked. getOfferings is also a non-interactive fetch that
       on a frozen bridge never settles, spinning Subscribe forever. So: race
       getOfferings against a 12s deadline that maps to an honest 'failed' (never
       'cancelled'); then try the Offering (best attribution when configured) and fall
       back to a direct StoreKit product fetch by id, the same dashboard-free path the
       packs use. The interactive purchasePackage / purchaseStoreProduct below are
       deliberately NOT deadline-raced. */
    const OFFERINGS_TIMEOUT = Symbol('offerings-timeout');
    const offerings = await Promise.race([
      Purchases.getOfferings(),
      new Promise<typeof OFFERINGS_TIMEOUT>((resolve) => setTimeout(() => resolve(OFFERINGS_TIMEOUT), 12000)),
    ]);
    if (offerings === OFFERINGS_TIMEOUT) {
      console.error('[iap] offerings fetch timed out');
      return 'failed';
    }
    const findIn = (o?: { availablePackages: Array<{ product: { identifier: string } }> } | null) =>
      o?.availablePackages.find(p => p.product.identifier === productId);
    const pkg = findIn(offerings.current) ?? findIn((offerings.all || {})['default']);
    if (pkg) {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg as never });
      return Object.keys(customerInfo.entitlements.active).length > 0 ? 'entitled' : 'pending';
    }
    /* Offering carried no matching package · buy the product directly by id. */
    const { products } = await Purchases.getProducts({
      productIdentifiers: [productId],
      type: PRODUCT_CATEGORY.SUBSCRIPTION,
    });
    const product = (products || []).find((p) => p.identifier === productId);
    if (!product) {
      console.error('[iap] subscription product not in StoreKit', productId);
      return 'failed';
    }
    const { customerInfo } = await Purchases.purchaseStoreProduct({ product });
    return Object.keys(customerInfo.entitlements.active).length > 0 ? 'entitled' : 'pending';
  } catch (e) {
    const err = e as { code?: unknown; message?: unknown };
    try {
      const { PURCHASES_ERROR_CODE } = await import('@revenuecat/purchases-capacitor');
      if (String(err?.code) === String(PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR)) return 'cancelled';
    } catch { /* fall through to message sniff */ }
    if (/cancel/i.test(String(err?.message || ''))) return 'cancelled';
    console.error('[iap] purchase failed', err?.code, err?.message);
    return 'failed';
  }
}

/* Consumable purchase · pull packs (2026-07-27, App Review 3.1.1). A
   consumable NEVER flips an entitlement, so the subscription purchase() above
   is the wrong shape for packs (its entitled/pending split reads webhook lag
   into a pack buy). Product ids are FINAL in App Store Connect:
   io.repvantage.wedge.pulls.1/.5/.10/.20/.50. Lookup goes through
   getProducts (direct StoreKit product fetch) so a pack buy does NOT depend
   on the RevenueCat offerings dashboard being configured for consumables;
   RevenueCat still records the transaction and forwards the receipt to the
   backend webhook, which grants the bonus pulls. The caller polls /usage for
   the balance to land · 'purchased' only ever means "StoreKit completed the
   transaction", never "pulls granted". */
export type ConsumableOutcome = 'purchased' | 'cancelled' | 'failed';
export async function purchaseConsumable(productId: string): Promise<ConsumableOutcome> {
  try {
    const { Purchases, PRODUCT_CATEGORY } = await import('@revenuecat/purchases-capacitor');
    /* 2026-07-29 · getProducts is a NON-interactive StoreKit fetch (it runs
       BEFORE purchaseStoreProduct raises the Apple sheet). Same frozen-bridge
       hang class as the subscription path: race a 12s deadline that maps to an
       honest 'failed' so a stalled fetch cannot spin the pack-buy button
       forever. purchaseStoreProduct below is deliberately NOT deadline-raced. */
    const PRODUCTS_TIMEOUT = Symbol('products-timeout');
    const fetched = await Promise.race([
      Purchases.getProducts({
        productIdentifiers: [productId],
        /* Ignored on iOS per the SDK docs, but explicit for a future Android
           shell where the default SUBSCRIPTION category would miss consumables. */
        type: PRODUCT_CATEGORY.NON_SUBSCRIPTION,
      }),
      new Promise<typeof PRODUCTS_TIMEOUT>((resolve) => setTimeout(() => resolve(PRODUCTS_TIMEOUT), 12000)),
    ]);
    if (fetched === PRODUCTS_TIMEOUT) {
      console.error('[iap] consumable products fetch timed out', productId);
      return 'failed';
    }
    const { products } = fetched;
    const product = (products || []).find((p) => p.identifier === productId);
    if (!product) {
      console.error('[iap] consumable product not found', productId);
      return 'failed';
    }
    await Purchases.purchaseStoreProduct({ product });
    return 'purchased';
  } catch (e) {
    const err = e as { code?: unknown; message?: unknown };
    try {
      const { PURCHASES_ERROR_CODE } = await import('@revenuecat/purchases-capacitor');
      if (String(err?.code) === String(PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR)) return 'cancelled';
    } catch { /* fall through to message sniff */ }
    if (/cancel/i.test(String(err?.message || ''))) return 'cancelled';
    console.error('[iap] consumable purchase failed', err?.code, err?.message);
    return 'failed';
  }
}

/** True only when a restore actually surfaced an active entitlement — the old
 *  void return let the UI claim "Purchases restored" to reps with nothing. */
export async function restore(): Promise<boolean> {
  /* 2026-07-29 · same never-settles class as the push bug (build 32-37): a hung
     RevenueCat bridge would spin the App-Review-required "Restore purchases"
     button forever with no catch. Race the work against a 20s deadline that
     resolves to "nothing restored", and swallow rejections to the same safe
     false, so the button always comes back. */
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const work = Purchases.restorePurchases().then(
      ({ customerInfo }) => Object.keys(customerInfo.entitlements.active).length > 0,
    );
    const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), 20000));
    return await Promise.race([work, timeout]);
  } catch (e) {
    console.warn('[iap] restore failed', e);
    return false;
  }
}

// ---- Sign in with Apple (required by Guideline 4.8 because Google sign-in is offered) ----
export async function signInWithApple() {
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
  const res = await SignInWithApple.authorize({
    clientId: 'io.repvantage.wedge',
    redirectURI: 'https://wedge.repvantage.io/auth/apple/callback',
    scopes: 'email name',
  });
  return res.response; // { identityToken, authorizationCode, email, ... } -> send to backend to mint a session
}

// The RepVantage Wedge iOS Google OAuth client (project RepVantage Drive). PUBLIC
// identifiers (an iOS OAuth client has no secret); they ship in the app binary by
// design. The reversed client id is the custom URL scheme registered in Info.plist.
const GOOGLE_IOS_CLIENT_ID = '928779495002-is9h3gufj56rs86f9oj24lo8cisb6qfn.apps.googleusercontent.com';
const GOOGLE_IOS_REVERSED = 'com.googleusercontent.apps.928779495002-is9h3gufj56rs86f9oj24lo8cisb6qfn';
const GOOGLE_REDIRECT_URI = GOOGLE_IOS_REVERSED + ':/oauth2redirect';

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function randToken(len = 48): string {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return b64url(arr);
}

// Sign in with Google — NATIVE (iOS) path. Google 403s OAuth inside an embedded
// WKWebView (disallowed_useragent), so we run the standard installed-app flow
// (Authorization Code + PKCE, no client secret) through the EXISTING @capacitor/
// browser plugin (opens ASWebAuthenticationSession / SFSafariViewController) and
// catch the redirect back to our reversed-client-id URL scheme via @capacitor/app.
// Deliberately NO new native plugin: Browser + App already ship in the shell, so
// there is no Package.swift change and zero blast radius on Sign-in-with-Apple or
// RevenueCat purchases. All plugins reached via registerPlugin (the codebase's
// safe pattern). Returns the Google id_token for /api/auth/google/credential-native,
// or null on cancel / any failure (never throws to the render path).
export async function signInWithGoogle(): Promise<{ idToken: string } | null> {
  if (!isNativeIOS()) return null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const Browser = registerPlugin<{ open(o: { url: string }): Promise<void>; close(): Promise<void> }>('Browser');
    const App = registerPlugin<{ addListener(name: string, cb: (d: unknown) => void): Promise<{ remove(): Promise<void> }> }>('App');

    const codeVerifier = randToken(48);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
    const codeChallenge = b64url(new Uint8Array(digest));
    const state = randToken(16);
    const nonce = randToken(16);

    const authUrl = 'https://accounts.google.com/o/oauth2/v2/auth?' + new URLSearchParams({
      client_id: GOOGLE_IOS_CLIENT_ID,
      redirect_uri: GOOGLE_REDIRECT_URI,
      response_type: 'code',
      scope: 'openid email profile',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
      nonce,
      prompt: 'select_account',
    }).toString();

    const code: string | null = await new Promise<string | null>((resolve) => {
      let handle: { remove(): Promise<void> } | null = null;
      let settled = false;
      const finish = (v: string | null) => {
        if (settled) return; settled = true;
        try { handle?.remove(); } catch { /* noop */ }
        try { void Browser.close(); } catch { /* noop */ }
        resolve(v);
      };
      const timer = setTimeout(() => finish(null), 180000); // 3 min guard
      void App.addListener('appUrlOpen', (data: unknown) => {
        try {
          const url = String((data as { url?: string })?.url || '');
          if (url.indexOf(GOOGLE_IOS_REVERSED) !== 0) return; // not our redirect
          clearTimeout(timer);
          const q = new URLSearchParams(url.split('?')[1] || '');
          finish(q.get('state') === state ? q.get('code') : null);
        } catch { clearTimeout(timer); finish(null); }
      }).then((h) => { handle = h; }).catch(() => {});
      Browser.open({ url: authUrl }).catch(() => { clearTimeout(timer); finish(null); });
    });
    if (!code) return null;

    // Exchange the code for an id_token · PKCE, no client secret (iOS public client).
    const tokRes = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: GOOGLE_IOS_CLIENT_ID,
        code,
        code_verifier: codeVerifier,
        grant_type: 'authorization_code',
        redirect_uri: GOOGLE_REDIRECT_URI,
      }).toString(),
    });
    if (!tokRes.ok) return null;
    const tok = (await tokRes.json()) as { id_token?: string };
    return tok?.id_token ? { idToken: tok.id_token } : null;
  } catch {
    return null;
  }
}

// The native Google flow only needs the Browser plugin, which ships in the iOS
// shell, so this is true on any native build and false on web · the button shows
// on iOS and stays hidden on the web landing.
export async function googleNativeAvailable(): Promise<boolean> {
  if (!isNativeIOS()) return false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    return typeof Capacitor?.isPluginAvailable === 'function' && Capacitor.isPluginAvailable('Browser');
  } catch {
    return false;
  }
}

const MICROSOFT_IOS_CLIENT_ID = '1933a1fb-0270-4560-99df-305ca9fb8c10';
const MICROSOFT_REDIRECT_URI = 'msauth.io.repvantage.wedge://auth';

// Sign in with Microsoft — NATIVE (iOS) path. Same installed-app Authorization
// Code + PKCE flow as Google (public client, no client secret) through the
// EXISTING @capacitor/browser + @capacitor/app plugins, catching the redirect on
// the msauth.io.repvantage.wedge:// custom scheme (registered in the iOS
// Info.plist). Deliberately NO new native plugin. Returns the Microsoft id_token
// for /api/auth/microsoft/credential-native, or null on cancel / any failure
// (never throws to the render path).
export async function signInWithMicrosoft(): Promise<{ idToken: string } | null> {
  if (!isNativeIOS()) return null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const Browser = registerPlugin<{ open(o: { url: string }): Promise<void>; close(): Promise<void> }>('Browser');
    const App = registerPlugin<{ addListener(name: string, cb: (d: unknown) => void): Promise<{ remove(): Promise<void> }> }>('App');

    const codeVerifier = randToken(48);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
    const codeChallenge = b64url(new Uint8Array(digest));
    const state = randToken(16);
    const nonce = randToken(16);

    const authUrl = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize?' + new URLSearchParams({
      client_id: MICROSOFT_IOS_CLIENT_ID,
      redirect_uri: MICROSOFT_REDIRECT_URI,
      response_type: 'code',
      response_mode: 'query',
      scope: 'openid profile email User.Read',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
      nonce,
      prompt: 'select_account',
    }).toString();

    const code: string | null = await new Promise<string | null>((resolve) => {
      let handle: { remove(): Promise<void> } | null = null;
      let settled = false;
      const finish = (v: string | null) => {
        if (settled) return; settled = true;
        try { handle?.remove(); } catch { /* noop */ }
        try { void Browser.close(); } catch { /* noop */ }
        resolve(v);
      };
      const timer = setTimeout(() => finish(null), 180000); // 3 min guard
      void App.addListener('appUrlOpen', (data: unknown) => {
        try {
          const url = String((data as { url?: string })?.url || '');
          if (url.indexOf('msauth.io.repvantage.wedge://') !== 0) return; // not our redirect
          clearTimeout(timer);
          const q = new URLSearchParams(url.split('?')[1] || '');
          finish(q.get('state') === state ? q.get('code') : null);
        } catch { clearTimeout(timer); finish(null); }
      }).then((h) => { handle = h; }).catch(() => {});
      Browser.open({ url: authUrl }).catch(() => { clearTimeout(timer); finish(null); });
    });
    if (!code) return null;

    // Exchange the code for an id_token · PKCE, no client secret (iOS public client).
    const tokRes = await fetch('https://login.microsoftonline.com/common/oauth2/v2.0/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: MICROSOFT_IOS_CLIENT_ID,
        code,
        code_verifier: codeVerifier,
        grant_type: 'authorization_code',
        redirect_uri: MICROSOFT_REDIRECT_URI,
      }).toString(),
    });
    if (!tokRes.ok) return null;
    const tok = (await tokRes.json()) as { id_token?: string };
    return tok?.id_token ? { idToken: tok.id_token } : null;
  } catch {
    return null;
  }
}

// True on any native build (only needs the Browser plugin, which ships in the
// shell); false on web so the button uses the web redirect there.
export async function microsoftNativeAvailable(): Promise<boolean> {
  if (!isNativeIOS()) return false;
  try {
    const { Capacitor } = await import('@capacitor/core');
    return typeof Capacitor?.isPluginAvailable === 'function' && Capacitor.isPluginAvailable('Browser');
  } catch {
    return false;
  }
}

// Connect Google Calendar — NATIVE (iOS) path. The web calendar connect runs a
// browser redirect that cannot round-trip from capacitor://, so on iOS we run
// the SAME installed-app PKCE flow signInWithGoogle uses (Browser +
// reversed-client-id redirect via @capacitor/app · no new native plugin, zero
// Package.swift / SIWA / purchase blast radius), but with the calendar read
// scope and access_type=offline + prompt=consent so Google returns a
// refresh_token (the backend needs it to refresh past the ~1h access-token
// life). We exchange the code ourselves, then hand access + refresh to
// POST /api/integrations/oauth/google/native-connect, which verifies the token
// and stores it in the SAME LinkedAccount row the web callback writes. Returns
// {ok:true} only when the row is stored; {ok:false} on cancel / any failure so
// the caller can show an honest "couldn't connect", never a fake success.
export async function connectGoogleCalendarNative(): Promise<{ ok: boolean; reason?: string }> {
  if (!isNativeIOS()) return { ok: false, reason: 'not-native' };
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const Browser = registerPlugin<{ open(o: { url: string }): Promise<void>; close(): Promise<void> }>('Browser');
    const App = registerPlugin<{ addListener(name: string, cb: (d: unknown) => void): Promise<{ remove(): Promise<void> }> }>('App');

    const codeVerifier = randToken(48);
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(codeVerifier));
    const codeChallenge = b64url(new Uint8Array(digest));
    const state = randToken(16);

    const authUrl = 'https://accounts.google.com/o/oauth2/v2/auth?' + new URLSearchParams({
      client_id: GOOGLE_IOS_CLIENT_ID,
      redirect_uri: GOOGLE_REDIRECT_URI,
      response_type: 'code',
      // openid+email so the connect resolves the account; calendar.readonly
      // covers getUpcomingEvents, calendar.events additionally lets the rep
      // send an invite from the Schedule Meeting sheet on iOS (2026-09-28
      // calendar invites wave). NOTE: the iOS Google OAuth client's consent
      // screen must list calendar.events or Google will silently drop it
      // from the granted set on this request.
      scope: 'openid email https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state,
      // offline + consent = Google issues a refresh_token (without these an
      // installed-app re-consent returns access-only and refresh dies at ~1h).
      access_type: 'offline',
      prompt: 'consent',
    }).toString();

    const code: string | null = await new Promise<string | null>((resolve) => {
      let handle: { remove(): Promise<void> } | null = null;
      let settled = false;
      const finish = (v: string | null) => {
        if (settled) return; settled = true;
        try { handle?.remove(); } catch { /* noop */ }
        try { void Browser.close(); } catch { /* noop */ }
        resolve(v);
      };
      const timer = setTimeout(() => finish(null), 180000);
      void App.addListener('appUrlOpen', (data: unknown) => {
        try {
          const url = String((data as { url?: string })?.url || '');
          if (url.indexOf(GOOGLE_IOS_REVERSED) !== 0) return;
          clearTimeout(timer);
          const q = new URLSearchParams(url.split('?')[1] || '');
          finish(q.get('state') === state ? q.get('code') : null);
        } catch { clearTimeout(timer); finish(null); }
      }).then((h) => { handle = h; }).catch(() => {});
      Browser.open({ url: authUrl }).catch(() => { clearTimeout(timer); finish(null); });
    });
    if (!code) return { ok: false, reason: 'cancelled' };

    const tokRes = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        client_id: GOOGLE_IOS_CLIENT_ID,
        code,
        code_verifier: codeVerifier,
        grant_type: 'authorization_code',
        redirect_uri: GOOGLE_REDIRECT_URI,
      }).toString(),
    });
    if (!tokRes.ok) return { ok: false, reason: 'token-exchange' };
    const tok = (await tokRes.json()) as { access_token?: string; refresh_token?: string; expires_in?: number; scope?: string };
    if (!tok?.access_token || !tok?.refresh_token) return { ok: false, reason: 'no-refresh-token' };

    // Hand tokens to the backend, which verifies + stores them (the shell fetch
    // shim rewrites the path to the prod origin and attaches the Bearer).
    const res = await fetch('/api/integrations/oauth/google/native-connect', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        accessToken: tok.access_token,
        refreshToken: tok.refresh_token,
        expiresIn: tok.expires_in || 3600,
        scopes: typeof tok.scope === 'string' && tok.scope ? tok.scope.split(' ').filter(Boolean) : undefined,
      }),
    });
    if (!res.ok) return { ok: false, reason: 'store-' + res.status };
    return { ok: true };
  } catch {
    return { ok: false, reason: 'unknown' };
  }
}

// ---- Haptics (native feel · no-op on web) ----
// Native side ships via @capacitor/haptics in the shell's deps; registerPlugin
// gives us the proxy without adding the package to the web app's tree.
export type HapticKind = 'light' | 'medium' | 'success' | 'warning' | 'error' | 'selection';
export function haptic(kind: HapticKind = 'light'): void {
  if (!isNativeIOS()) return;
  import('@capacitor/core')
    .then(({ registerPlugin }) => {
      const H = registerPlugin<{
        impact(opts: { style: string }): Promise<void>;
        notification(opts: { type: string }): Promise<void>;
        selectionStart(): Promise<void>;
        selectionChanged(): Promise<void>;
        selectionEnd(): Promise<void>;
      }>('Haptics');
      if (kind === 'light' || kind === 'medium') {
        void H.impact({ style: kind === 'light' ? 'LIGHT' : 'MEDIUM' });
      } else if (kind === 'selection') {
        void H.selectionStart().then(() => H.selectionChanged()).then(() => H.selectionEnd());
      } else {
        void H.notification({ type: kind.toUpperCase() });
      }
    })
    .catch(() => { /* haptics are garnish · never surface a failure */ });
}

// ---- WedgeSession: keep-awake + sun brightness (UX30 C3/C5) ----
// The native half is an app-local Swift plugin (WedgeSession.swift, ships on
// the ci/ios-build lineage · see the PR body for the exact snippet). Until it
// lands, registerPlugin returns a proxy whose calls reject "not implemented"
// and every path below swallows that: graceful no-op on today's main shell,
// lights up on the ci rebase with zero JS changes.
function wedgeSessionCall(method: 'keepAwake' | 'sunBoost', on: boolean): void {
  if (!isNativeIOS()) return;
  import('@capacitor/core')
    .then(({ registerPlugin }) => {
      const S = registerPlugin<{
        keepAwake(opts: { on: boolean }): Promise<void>;
        sunBoost(opts: { on: boolean }): Promise<void>;
      }>('WedgeSession');
      void S[method]({ on }).catch(() => { /* plugin absent · no-op */ });
    })
    .catch(() => { /* core absent · no-op */ });
}

/* Ref-counted keep-awake so overlapping arm sites (a brief open UNDER Drive
   Mode) cannot fight each other: the screen stays awake while ANY tag is
   armed and releases when the last one clears.
   BATTERY LANDMINE (spec C3): always disarm when the app leaves the
   foreground · the native side also disarms on background as belt and
   suspenders, and re-arming on return is driven by the still-armed tags. */
const awakeTags = new Set<string>();
function syncKeepAwake(): void {
  const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
  wedgeSessionCall('keepAwake', visible && awakeTags.size > 0);
}
export function requestKeepAwake(tag: string, on: boolean): void {
  const key = String(tag || 'app');
  if (on) awakeTags.add(key); else awakeTags.delete(key);
  syncKeepAwake();
}
export function sunBoost(on: boolean): void {
  wedgeSessionCall('sunBoost', !!on);
}

/** Foreground guards + the prototype-facing event contract. The bundle (no
 *  imports) arms via `window.__WEDGE_NATIVE__.keepAwake(on, tag)` or by
 *  dispatching `wedge:keepawake` with `{ on, tag }`; both funnel through the
 *  same ref-count. Call once from main.tsx alongside the other installers. */
export function installWedgeSessionGuards(): void {
  if (!isNativeIOS()) return;
  document.addEventListener('visibilitychange', syncKeepAwake);
  window.addEventListener('pagehide', () => wedgeSessionCall('keepAwake', false));
  window.addEventListener('wedge:keepawake', ((e: CustomEvent<{ on?: boolean; tag?: string }>) => {
    const d = e && e.detail ? e.detail : {};
    requestKeepAwake(d.tag || 'app', d.on === true);
  }) as EventListener);
}

// ---- Native share sheet shim (UX30 C12) ----
// Four prototype surfaces call navigator.share, whose WKWebView support is
// inconsistent. Shimming at the navigator.share level means ZERO prototype
// call-site changes. Honesty contract (#424): a cancelled share must reject
// with AbortError so no call site claims a save that never happened · the
// Swift side reports completed:false on cancel and the shim converts it.
// File payloads (the field-card PNG) keep the original WebKit sheet when one
// exists · the app-local plugin ships text/url only.
export function installNativeShareShim(): void {
  if (!isNativeIOS()) return;
  const nav = navigator as Navigator & { share?: (data?: ShareData) => Promise<void> };
  const orig = typeof nav.share === 'function' ? nav.share.bind(navigator) : null;
  nav.share = async (data?: ShareData): Promise<void> => {
    const d = data || {};
    const files = (d as { files?: unknown[] }).files;
    if (Array.isArray(files) && files.length && orig) return orig(d);
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const P = registerPlugin<{
        share(opts: { title?: string; text?: string; url?: string }): Promise<{ completed?: boolean }>;
      }>('WedgeShare');
      /* 2026-07-29 · this shim REPLACES navigator.share, so a hung WedgeShare
         bridge would hang every share caller in the app. Race an 8s deadline
         and fall back to the WebKit sheet on a stall, same hardening as the
         push and restore paths. */
      const TIMEOUT = Symbol('share-timeout');
      const res = await Promise.race([
        P.share({ title: d.title, text: d.text, url: d.url }),
        new Promise<typeof TIMEOUT>((resolve) => setTimeout(() => resolve(TIMEOUT), 8000)),
      ]);
      if (res === TIMEOUT) {
        if (orig) return orig(d);
        return;
      }
      if (res && (res as { completed?: boolean }).completed === false) {
        const err = new Error('Share canceled');
        err.name = 'AbortError';
        throw err;
      }
      return;
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw e;
      /* Plugin absent (today's main shell) or errored · fall back to the
         WebKit sheet when it exists so behavior never regresses. */
      if (orig) return orig(d);
      throw e instanceof Error ? e : new Error('Share unavailable');
    }
  };
}

/* ---- Local notifications (UX30 C13) · REMOVED 2026-07-28 ----------------
   scheduleLocalNotification / cancelLocalNotification lived here targeting a
   'WedgeLocalNotice' plugin. Audit finding: no such Swift plugin was ever
   written, RVBridgeViewController deliberately never registered one, and no
   code in the app ever called either function. Dead on both ends.

   This is the same shape as #440, where JS shipped for four Swift plugins
   that did not exist and the calls were silently swallowed for weeks. Keeping
   an exported API that can only ever return false is how the next reader
   concludes local notifications work here and builds a Settings toggle on
   top of nothing. Deleted rather than left dark: when C13 is actually wanted,
   the Swift half lands first and the caller lands with it.
   ---------------------------------------------------------------------- */

/* ---- Build identity, readable from the phone ----------------------------
   Twenty-three consecutive builds shipped a stale web bundle and nobody
   caught it, because from inside the app there was no way to tell one build
   from another. window.__WEDGE_BUILD__ has existed the whole time and was
   never rendered anywhere. The CI freshness gate stops NEW stale builds; this
   is the other half, the one that lets a human standing in a parking lot
   answer "am I actually looking at the new build?" in two taps.

   Populates window.__WEDGE_NATIVE_BUILD__ = { version, build } from the
   native container (CFBundleShortVersionString / CFBundleVersion, which is
   the TestFlight build number). The prototype has no imports, so a window
   global is the only channel it can read. */
export function initNativeBuildInfo(): void {
  if (!isNativeIOS()) return;
  void (async () => {
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const App = registerPlugin<{ getInfo(): Promise<{ version: string; build: string }> }>('App');
      /* 2026-07-29 · deadline-guard the App.getInfo await. Same frozen-bridge
         class as the push boot path: a never-settling getInfo would leak a
         pending task on every cold launch. On timeout, leave the web build
         stamp in place rather than hang. */
      const info = await withDeadline<{ version?: string; build?: string } | null>(App.getInfo(), 4000, null);
      if (!info) return;
      (window as unknown as Record<string, unknown>).__WEDGE_NATIVE_BUILD__ = {
        version: info.version || '',
        build: info.build || '',
      };
    } catch {
      /* Web, or the App plugin is unavailable. The Settings row falls back to
         the web build stamp rather than inventing a build number. */
    }
  })();
}

// ---- External links: in-app Safari sheet instead of bouncing to Safari ----
// Capacitor's default for window.open / target=_blank / external top-level
// navigation is UIApplication.open (system browser) — safe, but it throws the
// rep out of the app for every brief source link. On native we intercept
// plain web-content links and show them in SFSafariViewController
// (@capacitor/browser) with a Done button. System-path hosts are exempt on
// purpose: maps links should open the Maps app, apps.apple.com must reach the
// App Store (subscription management), and tel:/mailto:/sms:/shortcuts: are
// not http at all.
const SYSTEM_PATH_HOSTS = [
  'maps.google.com', 'www.google.com', 'maps.apple.com', 'goo.gl',
  'maps.app.goo.gl', 'g.co',
  'apps.apple.com', 'itunes.apple.com', 'testflight.apple.com',
];
function isSystemPathUrl(url: URL): boolean {
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return true;
  const host = url.hostname.toLowerCase();
  /* www.google.com only counts for /maps paths · other google links are web content */
  if (host === 'www.google.com') return url.pathname.startsWith('/maps');
  return SYSTEM_PATH_HOSTS.includes(host);
}

export function installNativeLinkPolish(): void {
  if (!isNativeIOS()) return;
  const openInSheet = (raw: string): boolean => {
    try {
      const url = new URL(raw, window.location.href);
      if (isSystemPathUrl(url)) return false;
      /* our own app origin navigations stay in the webview */
      if (url.origin === window.location.origin) return false;
      import('@capacitor/core')
        .then(({ registerPlugin }) => {
          const B = registerPlugin<{ open(opts: { url: string }): Promise<void> }>('Browser');
          return B.open({ url: url.toString() });
        })
        .catch(() => { window.location.assign(url.toString()); /* Capacitor bounces external to system */ });
      return true;
    } catch {
      return false;
    }
  };
  /* window.open chokepoint (Terms/Privacy buttons, share fallbacks, …).
     Return a benign stub, NOT null (2026-07-27): several call sites read a
     null return as "popup blocked" and fire a same-tab fallback — that
     pattern double-opened externals and pinned rvOpenMaps-style helpers to
     their blocked branch. */
  const openStub = { closed: true, close: () => undefined, focus: () => undefined } as unknown as Window;
  const realOpen = window.open.bind(window);
  window.open = ((url?: string | URL, target?: string, features?: string) => {
    const raw = String(url || '');
    if (raw && openInSheet(raw)) return openStub;
    return realOpen(url as string, target, features);
  }) as typeof window.open;
  /* anchor chokepoint (brief source links render as <a target="_blank">).
     Guards (2026-07-27, adversarial review): never hijack programmatic
     detached-anchor clicks (the Gmail-compose handoff builds one), download
     anchors, already-handled events, or modified/non-primary clicks. */
  document.addEventListener(
    'click',
    (e) => {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.('a[href]');
      if (!a) return;
      if (!a.isConnected) return;
      if (a.hasAttribute('download')) return;
      const href = a.getAttribute('href') || '';
      if (!/^https?:/i.test(href)) return;
      if (openInSheet(href)) { e.preventDefault(); e.stopPropagation(); }
    },
    true,
  );
}

// ---- Native push notifications (APNs) ----
// The native side ships via @capacitor/push-notifications in the shell's deps;
// registerPlugin gives us the proxy without adding the package to the web
// app's tree (same pattern as Haptics/Browser/App above). The server side is
// DARK until an APNs key exists, so a registered token is inert but ready:
// storing it now means the first server send Just Works when the key lands.
export type NativePushPerm = 'granted' | 'denied' | 'prompt' | 'unavailable';

interface PushNotificationsPlugin {
  checkPermissions(): Promise<{ receive: string }>;
  requestPermissions(): Promise<{ receive: string }>;
  register(): Promise<void>;
  addListener(event: 'registration', cb: (token: { value: string }) => void): unknown;
  addListener(event: 'registrationError', cb: (err: unknown) => void): unknown;
}

const normalizePushPerm = (receive: string | undefined): NativePushPerm =>
  receive === 'granted' ? 'granted' : receive === 'denied' ? 'denied' : 'prompt';

async function getPushPlugin(): Promise<PushNotificationsPlugin> {
  const { registerPlugin } = await import('@capacitor/core');
  return registerPlugin<PushNotificationsPlugin>('PushNotifications');
}

/* Listeners are process-wide on the plugin proxy · install exactly once no
   matter how many times enable/init run, or one grant would upload N times. */
let pushListenersInstalled = false;
function installPushListeners(push: PushNotificationsPlugin): void {
  if (pushListenersInstalled) return;
  pushListenersInstalled = true;
  push.addListener('registration', ({ value }) => {
    /* The shell fetch shim rewrites the path to the prod origin and attaches
       the Bearer. 404 (endpoint not deployed yet) and network errors fail
       silent by design · push is dark until the backend sender ships. */
    fetch('/api/wedge/push/native-register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: value, platform: 'ios' }),
    })
      .then((res) => {
        if (!res.ok) console.warn('[native] push token registration declined', res.status);
      })
      .catch((e) => console.warn('[native] push token registration failed', e));
  });
  push.addListener('registrationError', (err) => {
    console.warn('[native] APNs registration error', err);
  });
}

/* 2026-07-29 · app-local WedgePush is the ONLY push path on iOS now.
   Builds 32-36 could not turn notifications on. Confirmed live on a device
   2026-07-29: tapping the toggle produced NO iOS permission sheet, which means
   WedgePush.enable() (the call that raises the sheet) was never reached. Two
   things routed every tap away from it, both removed here:

   1. getWedgePushPlugin() gated on Capacitor.isPluginAvailable('WedgePush')
      and returned null when it read false - which on device it did. That gate
      was added build 35 on a FALSE premise: its comment claimed an
      unregistered proxy "silently swallows every call", but @capacitor/core
      REJECTS a method on a plugin missing from PluginHeaders with "not
      implemented on ios" (see createPluginMethod in the core runtime). It does
      not hang. So the gate was pure downside - it hid the working plugin.
   2. When the gate returned null, both callers fell through to
      @capacitor/push-notifications, whose native call has never settled since
      build 32. That frozen path is the actual hang the rep saw.

   Now: call WedgePush directly (a genuinely-absent plugin rejects fast and is
   caught by the callers, naming the failure); NO fallback to the package
   plugin. WedgePush.enable() always resolves - it has its own 15s APNs
   deadline - so once it is actually reached the toggle can no longer hang. */
function withDeadline<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

/* Last push diagnostic, readable from the phone as window.__WEDGE_PUSH_DIAG__.
   This Mac cannot build or run iOS, so a value the device itself carries is the
   cheapest way to name WHICH step failed on the next build, cable or not. */
function setPushDiag(d: Record<string, unknown>): void {
  try {
    (window as unknown as Record<string, unknown>).__WEDGE_PUSH_DIAG__ = d;
  } catch { /* diagnostics only, never throw into the toggle */ }
}
const pushErr = (e: unknown): string => String((e as { message?: unknown })?.message ?? e);

async function getWedgePushPlugin(): Promise<{
  status(): Promise<{ receive?: string }>;
  enable(): Promise<{ receive?: string; token?: string; reason?: string }>;
} | null> {
  try {
    const { registerPlugin } = await import('@capacitor/core');
    return registerPlugin('WedgePush');
  } catch {
    return null;
  }
}

export async function checkNativePushPermission(): Promise<NativePushPerm> {
  if (!isNativeIOS()) return 'unavailable';
  const local = await getWedgePushPlugin();
  if (!local) { setPushDiag({ step: 'check', error: 'core-import-failed' }); return 'unavailable'; }
  try {
    const r = await withDeadline(local.status(), 4000, { receive: 'js-timeout' });
    setPushDiag({ step: 'check', receive: r?.receive });
    return normalizePushPerm(r?.receive);
  } catch (e) {
    setPushDiag({ step: 'check', error: pushErr(e) });
    return 'unavailable';
  }
}

/** Explicit opt-in: prompts for permission (first time only), then registers
 *  with APNs and uploads the device token. Returns the resulting permission
 *  state so the caller's toggle can reflect reality, not intent. */
export async function enableNativePush(): Promise<NativePushPerm> {
  if (!isNativeIOS()) return 'unavailable';
  const local = await getWedgePushPlugin();
  if (!local) { setPushDiag({ step: 'enable', error: 'core-import-failed' }); return 'unavailable'; }
  try {
    /* One call does prompt + register + token, and always resolves with a
       named outcome. 20s outlasts the plugin's own 15s APNs deadline so the
       native reason survives instead of being masked by a JS timeout. */
    const r = await withDeadline(local.enable(), 20000, { receive: 'prompt', reason: 'js-deadline' });
    if (r?.reason) console.warn('[native] WedgePush.enable:', r.reason);
    setPushDiag({ step: 'enable', receive: r?.receive, reason: r?.reason || '' });
    const token = typeof r?.token === 'string' ? r.token.trim() : '';
    if (token) {
      /* Same endpoint and shape installPushListeners uses. Failing to bank the
         token must not fail the toggle: permission is already granted and the
         boot path re-registers, so a network blip costs a retry, not the grant. */
      try {
        await fetch('/api/wedge/push/native-register', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, platform: 'ios' }),
        });
      } catch { /* offline · boot path re-registers */ }
    }
    return normalizePushPerm(r?.receive);
  } catch (e) {
    console.warn('[native] WedgePush.enable rejected', pushErr(e));
    setPushDiag({ step: 'enable', error: pushErr(e) });
    return 'unavailable';
  }
}

/** Boot path: NEVER prompts. Only re-registers (APNs tokens rotate across
 *  reinstalls and OS updates) when the rep already granted permission via the
 *  explicit toggle. Safe to call unconditionally from main.tsx. */
export function initNativePush(): void {
  if (!isNativeIOS()) return;
  void (async () => {
    try {
      /* 2026-08-04 (TF86 · zero-tokens incident) · boot re-registration now
         rides WedgePush, the plugin that actually answers on device. The old
         path asked the frozen @capacitor/push-notifications bridge (builds
         32-37: checkPermissions never settled behind its deadline), so a
         GRANTED device never re-banked its token · prod held zero
         wedge_apns_token rows and nginx showed zero register hits while the
         founder waited on a brief-ready ping. status() is read first so this
         can never cold-fire the permission sheet: enable() runs ONLY when
         iOS already says granted, and in that state it registers + returns a
         fresh token without prompting (APNs tokens rotate across reinstalls
         and OS updates, so every cold launch re-banks). An un-granted device
         keeps the explicit Brief-ready toggle as its only prompt path. */
      const local = await getWedgePushPlugin();
      if (!local) { setPushDiag({ step: 'boot', error: 'core-import-failed' }); return; }
      const st = await withDeadline(local.status(), 4000, { receive: 'js-timeout' });
      if (normalizePushPerm(st?.receive) !== 'granted') { setPushDiag({ step: 'boot', receive: st?.receive }); return; }
      const r = await withDeadline(local.enable(), 20000, { receive: 'granted', reason: 'js-deadline' });
      setPushDiag({ step: 'boot', receive: r?.receive, reason: r?.reason || '' });
      const token = typeof r?.token === 'string' ? r.token.trim() : '';
      if (!token) return;
      await fetch('/api/wedge/push/native-register', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, platform: 'ios' }),
      });
    } catch (e) {
      console.warn('[native] push re-registration skipped', e);
    }
  })();
}

// ---- Live Activity (Lock Screen / Dynamic Island brief-streaming card) ----
// Native side is the app-local LiveActivityPlugin registered by
// RVBridgeViewController (apps/wedge-ios/ios/App/App/LiveActivityPlugin.swift)
// and rendered by the RepVantageActivity WidgetKit extension. Every call is
// fire-and-forget garnish: the plugin resolves (never rejects) and iOS < 16.2
// simply reports unsupported. Updates are throttled here because ActivityKit
// enforces a per-activity update budget; stage changes always pass.
interface LiveActivityPluginProxy {
  start(opts: { accountName: string; stage: string; detail: string; progress: number }): Promise<{ ok?: boolean }>;
  update(opts: { stage: string; detail: string; progress: number }): Promise<unknown>;
  end(opts: { stage: string; detail: string; progress: number; dismissAfter?: number }): Promise<unknown>;
}
async function getLiveActivityPlugin(): Promise<LiveActivityPluginProxy> {
  const { registerPlugin } = await import('@capacitor/core');
  return registerPlugin<LiveActivityPluginProxy>('LiveActivity');
}

/* Coarse progress model: the SSE stream has no numeric progress, so mirror
   the loading screen's heuristic: each distinct stage label nudges the ring
   toward 0.9; complete snaps to 1. Reset per start(). */
let laLastSentAt = 0;
let laLastStage = '';
let laSeenLabels: Set<string> | null = null;
let laActive = false;
let laSuppressed = false;

/** Build 14 audit (P1-1): the throttle state above and the Swift plugin's
 *  update/end loops are both effectively singletons, so two concurrent
 *  streams (batch prep runs 2 workers) fight over one card: B's start kills
 *  A's, A's text lands on B's title, the first finisher ends the survivor.
 *  Batch prep suppresses the Live Activity entirely for its duration. */
export function briefActivitySuppress(on: boolean): void {
  laSuppressed = on;
  if (on && laActive) briefActivityEnd(false, 'Batch prep running');
}

function laProgress(stage: string): number {
  if (stage === 'complete') return 1;
  const seen = laSeenLabels ? laSeenLabels.size : 0;
  return Math.min(0.9, 0.15 + seen * 0.11);
}

export function briefActivityStart(accountName: string, detail = 'Starting research...'): void {
  if (!isNativeIOS() || laSuppressed) return;
  laActive = true;
  laLastStage = 'searching';
  laLastSentAt = Date.now();
  laSeenLabels = new Set();
  getLiveActivityPlugin()
    .then((p) => p.start({ accountName, stage: 'searching', detail, progress: 0.08 }))
    /* Build 14 audit (P2-7): if activities are disabled (or unsupported) the
       plugin resolves ok:false · unlatch so the whole stream is not spent
       sending throttled no-op updates to a card that never existed. */
    .then((r) => { if (!r || r.ok !== true) laActive = false; })
    .catch(() => { laActive = false; });
}

export function briefActivityUpdate(stage: string, detail: string): void {
  if (!isNativeIOS() || !laActive) return;
  if (detail && laSeenLabels) laSeenLabels.add(detail);
  const stageChanged = stage !== laLastStage;
  const now = Date.now();
  /* ActivityKit budgets updates; 2.5s floor unless the stage itself moved. */
  if (!stageChanged && now - laLastSentAt < 2500) return;
  laLastStage = stage;
  laLastSentAt = now;
  getLiveActivityPlugin()
    .then((p) => p.update({ stage, detail: detail || '', progress: laProgress(stage) }))
    .catch(() => { /* garnish */ });
}

export function briefActivityEnd(ok: boolean, detail?: string): void {
  if (!isNativeIOS() || !laActive) return;
  laActive = false;
  getLiveActivityPlugin()
    .then((p) => p.end(
      ok
        ? { stage: 'complete', detail: detail || 'Brief ready · open RepVantage', progress: 1, dismissAfter: 8 }
        : { stage: 'failed', detail: detail || 'Research stopped', progress: 0, dismissAfter: 0 },
    ))
    .catch(() => { /* garnish */ });
}

// ---- Dynamic Type (@capacitor/text-zoom) ----
// The webview ships user-scalable=no, so the OS text size was ignored
// entirely. Map the native preferred zoom onto the page once at boot.
// Clamped at 1.35 (135%): the layout is verified to tolerate that; a 2x
// accessibility size would shatter the brief grid and hide CTAs, which is
// worse for the rep than a slightly-smaller-than-preferred font.
export function initNativeTextZoom(): void {
  if (!isNativeIOS()) return;
  void (async () => {
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const TZ = registerPlugin<{
        getPreferred(): Promise<{ value: number }>;
        set(opts: { value: number }): Promise<void>;
      }>('TextZoom');
      /* 2026-07-29 · deadline-guard getPreferred (non-interactive native read).
         A frozen bridge would leak a pending boot task; on timeout skip the
         zoom map, which is progressive enhancement anyway. */
      const pref = await withDeadline<{ value?: number } | null>(TZ.getPreferred(), 4000, null);
      const value = pref?.value;
      if (typeof value !== 'number' || !Number.isFinite(value) || value === 1) return;
      await TZ.set({ value: Math.min(1.35, Math.max(0.85, value)) });
    } catch { /* text zoom is progressive enhancement */ }
  })();
}

// ---- Keyboard (@capacitor/keyboard · native only) ----
// The plugin ships in the shell's deps; registerPlugin gives us the proxy
// without adding it to the web tree. Config resize mode is "none" on purpose
// (capacitor.config.json): the app worked for 13 builds with un-resized
// webview geometry, and "native" would reflow every fixed surface on every
// keyboard show. Instead we (a) hide the input-accessory bar while a short
// field (PIN / search / other numeric) is focused, (b) publish the keyboard
// height as a CSS variable (--rv-kb-h) so scrollable composers can pad
// themselves above the keyboard, and (c) nudge the focused field into view
// once the keyboard is up, because with resize "none" WebKit will not always
// do it inside inner scrollers.
interface KeyboardPluginProxy {
  setAccessoryBarVisible(opts: { isVisible: boolean }): Promise<void>;
  addListener(event: 'keyboardWillShow' | 'keyboardDidShow', cb: (info: { keyboardHeight: number }) => void): unknown;
  addListener(event: 'keyboardWillHide' | 'keyboardDidHide', cb: () => void): unknown;
}

const isShortFormField = (el: Element | null): boolean => {
  if (!el || !(el instanceof HTMLElement)) return false;
  const tag = el.tagName;
  if (tag !== 'INPUT') return false;
  const mode = (el.getAttribute('inputmode') || '').toLowerCase();
  const hint = (el.getAttribute('enterkeyhint') || '').toLowerCase();
  return mode === 'numeric' || mode === 'search' || mode === 'decimal' || mode === 'tel' || hint === 'search';
};

export function initNativeKeyboard(): void {
  if (!isNativeIOS()) return;
  void (async () => {
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const KB = registerPlugin<KeyboardPluginProxy>('Keyboard');
      /* Accessory bar: hidden for PIN/search style short fields (the grey
         prev/next/Done bar reads as web chrome there), shown for free-text
         fields where Done is the only sane way to dismiss the keyboard. */
      let accessoryHidden = false;
      const syncAccessory = () => {
        const wantHidden = isShortFormField(document.activeElement);
        if (wantHidden === accessoryHidden) return;
        accessoryHidden = wantHidden;
        void KB.setAccessoryBarVisible({ isVisible: !wantHidden }).catch(() => undefined);
      };
      document.addEventListener('focusin', syncAccessory, true);
      document.addEventListener('focusout', () => {
        /* focusout fires before the next focusin; defer so the read sees the
           element that actually ended up focused. */
        setTimeout(syncAccessory, 0);
      }, true);

      const setKb = (h: number) => {
        try {
          document.documentElement.style.setProperty('--rv-kb-h', `${Math.max(0, Math.round(h))}px`);
        } catch { /* style write blocked · padding is garnish */ }
      };
      KB.addListener('keyboardWillShow', (info) => setKb(info?.keyboardHeight || 0));
      KB.addListener('keyboardWillHide', () => setKb(0));
      KB.addListener('keyboardDidShow', () => {
        const el = document.activeElement;
        if (!el || !(el instanceof HTMLElement)) return;
        if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
        try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch { /* older WebKit */ }
      });
    } catch { /* keyboard polish is progressive enhancement */ }
  })();
}

// ---- Status bar (@capacitor/status-bar · native only) ----
// Two jobs: (1) statusTap scroll-to-top · the free native behavior only works
// on the window scroller and every wedge screen scrolls an inner div, so a
// status-bar tap did nothing; (2) explicit bar style following the app theme
// instead of whatever UIKit inferred. Theme source of truth is the
// .wedge-scope[data-theme] attribute (same read as the prototype's
// useWedgeThemeIsLight · NEVER documentElement, which stays "dark" for the
// pre-auth surface).
interface StatusBarPluginProxy {
  setStyle(opts: { style: 'DARK' | 'LIGHT' | 'DEFAULT' }): Promise<void>;
}

function findActiveScroller(): HTMLElement | null {
  /* Deepest visible element that (a) actually scrolls, (b) has been scrolled.
     Visibility check keeps hidden screens (display:none tab stacks) out. */
  const all = document.querySelectorAll<HTMLElement>('div, main, section, ul');
  let best: HTMLElement | null = null;
  let bestArea = 0;
  for (const el of Array.from(all)) {
    if (el.scrollTop <= 0) continue;
    if (el.scrollHeight - el.clientHeight < 40) continue;
    const r = el.getBoundingClientRect();
    if (r.width < 80 || r.height < 80 || r.bottom < 0 || r.top > window.innerHeight) continue;
    const st = window.getComputedStyle(el);
    if (st.overflowY !== 'auto' && st.overflowY !== 'scroll') continue;
    const area = Math.min(r.width, window.innerWidth) * Math.min(r.height, window.innerHeight);
    if (area >= bestArea) { bestArea = area; best = el; }
  }
  return best;
}

export function initNativeStatusBar(): void {
  if (!isNativeIOS()) return;
  void (async () => {
    try {
      const { registerPlugin } = await import('@capacitor/core');
      const SB = registerPlugin<StatusBarPluginProxy>('StatusBar');
      /* DARK style = light text · correct for the deep-field boot surface. */
      let lastLight: boolean | null = null;
      const apply = (themeIsLight: boolean) => {
        if (lastLight === themeIsLight) return;
        lastLight = themeIsLight;
        void SB.setStyle({ style: themeIsLight ? 'LIGHT' : 'DARK' }).catch(() => undefined);
      };
      apply(false);
      /* The scope element mounts with WedgeRoute (post-auth), so poll until
         it exists, then follow its data-theme attribute live. */
      let tries = 0;
      const attach = () => {
        const scope = document.querySelector('.wedge-scope[data-theme]');
        if (!scope) {
          if (tries++ < 120) setTimeout(attach, 1000);
          return;
        }
        const read = () => apply(scope.getAttribute('data-theme') !== 'dark');
        read();
        new MutationObserver(read).observe(scope, { attributes: true, attributeFilter: ['data-theme'] });
      };
      attach();

      window.addEventListener('statusTap', () => {
        const el = findActiveScroller();
        if (!el) return;
        let smooth = true;
        try { smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { /* default smooth */ }
        try { el.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' }); } catch { el.scrollTop = 0; }
      });
    } catch { /* status bar polish is progressive enhancement */ }
  })();
}

// ---- App Store rating prompt (app-local Rating plugin · SKStoreReview) ----
// Fired only from noteBriefSuccessForRating: after the 3rd FRESH successful
// brief, never on a failure path, at most once per app version. iOS itself
// further caps the system sheet at 3 prompts/365d, and in TestFlight builds
// the sheet never shows (by design · safe to ship dark).
const RATING_COUNT_KEY = 'rv_wedge_brief_success_n';
const RATING_ASKED_KEY = 'rv_wedge_rating_asked_ver';

export function noteBriefSuccessForRating(): void {
  if (!isNativeIOS()) return;
  let count = 0;
  try {
    count = (parseInt(localStorage.getItem(RATING_COUNT_KEY) || '0', 10) || 0) + 1;
    localStorage.setItem(RATING_COUNT_KEY, String(count));
  } catch { return; /* no storage = no counter = never nag */ }
  if (count < 3) return;
  /* Give the brief-ready moment room to land before the system sheet. */
  setTimeout(() => {
    void (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        const App = registerPlugin<{ getInfo(): Promise<{ version: string; build: string }> }>('App');
        /* 2026-07-29 · deadline-guard getInfo so a frozen App bridge can't leave
           this rating IIFE pending forever. On timeout, skip the prompt this
           time (the counter already persisted; it re-arms on the next brief). */
        const info = await withDeadline<{ version?: string; build?: string } | null>(App.getInfo(), 4000, null);
        if (!info) return;
        const ver = `${info.version || ''}-${info.build || ''}`;
        try {
          if (localStorage.getItem(RATING_ASKED_KEY) === ver) return;
          localStorage.setItem(RATING_ASKED_KEY, ver);
        } catch { return; }
        const Rating = registerPlugin<{ request(): Promise<{ ok?: boolean }> }>('Rating');
        /* Deadline-guard the SKStoreReview request too · it is fire-and-forget
           garnish, so a stalled sheet must not leak a pending task. */
        await withDeadline(Rating.request(), 8000, { ok: false });
      } catch { /* rating is garnish · never surface a failure */ }
    })();
  }, 2500);
}

// ---- Siri / URL-scheme deep links (repvantage://open?siri=...) ----
// The Swift App Intents (ios/App/App/SiriShortcuts.swift) route by opening the
// app's own repvantage:// scheme; Capacitor's App plugin surfaces that as an
// `appUrlOpen` event and retains it until a listener attaches, so a cold launch
// straight from Siri still lands here. The native App plugin ships with the
// shell (apps/wedge-ios deps), so registerPlugin gives us its proxy without
// adding @capacitor/app to the web app's dependency tree.
export function initNativeDeepLinks(): void {
  if (!isNativeIOS()) return;
  import('@capacitor/core')
    .then(({ registerPlugin }) => {
      const App = registerPlugin<{
        addListener(event: 'appUrlOpen', cb: (data: { url: string }) => void): unknown;
      }>('App');
      App.addListener('appUrlOpen', ({ url }) => {
        try {
          const params = new URL(url).searchParams;
          const siri = params.get('siri');
          /* Re-enter through the router so WedgeRoute's existing ?siri=
             deep-link parsing (next-meeting | drive | search) does the rest.
             All assets are local to the shell, so the reload is instant. */
          if (siri) {
            window.location.replace('/?siri=' + encodeURIComponent(siri));
            return;
          }
          /* App Entities / Spotlight / Siri "Open <account>" (AppEntities.swift
             OpenAccountIntent) opens repvantage://open?account=<id>. WedgeRoute
             already handles ?account= (jump straight into the brief). */
          const account = params.get('account');
          if (account) window.location.replace('/?account=' + encodeURIComponent(account));
        } catch (e) {
          console.warn('[native] unparseable deep link', url, e);
        }
      });
    })
    .catch((e) => console.warn('[native] deep-link listener failed to install', e));
}

// ---- Native content store (App Entities / Spotlight / iOS 26 snippets) ----
// The App Entities, Spotlight index and interactive snippets all run OUTSIDE
// the webview and cannot see window.COMPANIES / SAVED_ITEMS. The app-local
// WedgeStorePlugin (apps/wedge-ios/ios/App/App/WedgeStore.swift, registered by
// RVBridgeViewController) persists a BOUNDED mirror to Application Support that
// those surfaces read. The web layer feeds it on hydrate + save via
// syncNativeStore(); registerPlugin gives us the proxy with no new web dep.
// Persist NOTHING sensitive: id, name, address, kind, coord, score, savedAt and
// the brief headline lines only. No tokens, no full brief bodies.
export interface WedgeStoreEntry {
  id: string;
  name: string;
  address?: string;
  kind?: string;
  lat?: number;
  lng?: number;
  score?: number;
  savedAt?: string;
  headlines: string[];
}
interface WedgeStorePluginProxy {
  sync(opts: { entries: WedgeStoreEntry[] }): Promise<{ ok?: boolean; count?: number }>;
  read(): Promise<{ entries: WedgeStoreEntry[] }>;
}
async function getWedgeStorePlugin(): Promise<WedgeStorePluginProxy> {
  const { registerPlugin } = await import('@capacitor/core');
  return registerPlugin<WedgeStorePluginProxy>('WedgeStore');
}

const num = (v: unknown): number | undefined => {
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
};
const str = (v: unknown): string | undefined => {
  const s = typeof v === 'string' ? v.trim() : '';
  return s.length > 0 ? s : undefined;
};

/* Build one bounded snapshot entry from a SAVED_ITEMS row + its COMPANIES
   brief (if present in-session). Everything is defensive: the mirror is
   garnish and must never throw into the save path. */
function buildStoreEntry(
  saved: Record<string, unknown>,
  companies: Record<string, Record<string, unknown> | undefined>,
): WedgeStoreEntry | null {
  const id = str(saved.id);
  if (!id) return null;
  const snap = (saved.company && typeof saved.company === 'object'
    ? (saved.company as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const brief = companies[id] || {};
  const name = str(brief.name) || str(snap.name) || 'Saved account';
  const address = str(snap.address) || str(brief.address);
  const kind = str(snap.kind) || str(brief.kind) || str(snap.sector) || str(brief.sector);
  const lat = num(snap.lat) ?? num(brief.lat);
  const lng = num(snap.lng) ?? num(brief.lng);
  const score = num(brief.score) ?? num(snap.score);
  const savedAt = str(saved.savedAt) || str(saved.createdAt);
  const headlineSources: unknown[] = [
    brief.keyInsight,
    snap.keyInsight,
    brief.summary,
    snap.summary,
  ];
  const moves = Array.isArray(brief.moves)
    ? (brief.moves as Array<{ text?: unknown }>)
    : Array.isArray(snap.moves)
      ? (snap.moves as Array<{ text?: unknown }>)
      : [];
  for (const m of moves) headlineSources.push(m?.text);
  const headlines = headlineSources
    .map((h) => str(h))
    .filter((h): h is string => !!h)
    .map((h) => (h.length > 140 ? h.slice(0, 137) + '...' : h))
    .slice(0, 6);
  return { id, name, address, kind, lat, lng, score, savedAt, headlines };
}

let storeSyncTimer: ReturnType<typeof setTimeout> | null = null;
/** Mirror the rep's saved accounts/briefs into the native content store.
 *  No-op off-native. Coalesced (250ms) so a burst of saveSaved calls collapses
 *  into one native write; always fire-and-forget. */
export function syncNativeStore(): void {
  if (!isNativeIOS()) return;
  if (storeSyncTimer) clearTimeout(storeSyncTimer);
  storeSyncTimer = setTimeout(() => {
    storeSyncTimer = null;
    try {
      const w = window as unknown as {
        SAVED_ITEMS?: unknown;
        COMPANIES?: Record<string, Record<string, unknown> | undefined>;
      };
      const saved = Array.isArray(w.SAVED_ITEMS) ? (w.SAVED_ITEMS as Record<string, unknown>[]) : [];
      const companies = w.COMPANIES || {};
      const entries = saved
        .map((s) => buildStoreEntry(s, companies))
        .filter((e): e is WedgeStoreEntry => e !== null)
        .slice(0, 200);
      void getWedgeStorePlugin()
        .then((p) => p.sync({ entries }))
        .catch(() => { /* mirror is garnish */ });
    } catch { /* never break the save path */ }
  }, 250);
}

// ---- MapKit Look Around (pin-card street-level peek) ----
// App-local WedgeLookAroundPlugin (apps/wedge-ios/ios/App/App/WedgeLookAround.swift).
// probe() returns whether Apple has Look Around coverage at the coordinate so
// the pin card only renders the row when a scene actually resolves (never a
// dead button); open() presents MKLookAroundViewController in a medium sheet.
interface WedgeLookAroundProxy {
  probe(opts: { lat: number; lng: number }): Promise<{ available?: boolean }>;
  open(opts: { lat: number; lng: number }): Promise<{ ok?: boolean; reason?: string }>;
}
async function getLookAroundPlugin(): Promise<WedgeLookAroundProxy> {
  const { registerPlugin } = await import('@capacitor/core');
  return registerPlugin<WedgeLookAroundProxy>('WedgeLookAround');
}

/** True only when Apple has Look Around coverage at this coordinate. Always
 *  false off-native or on a bad/zero coordinate, so callers can safely gate a
 *  row on the resolved value. */
export async function lookAroundProbe(lat: number, lng: number): Promise<boolean> {
  if (!isNativeIOS()) return false;
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) return false;
  try {
    const p = await getLookAroundPlugin();
    /* 2026-07-29 · deadline-guard the probe. The pin-card Look Around row gates
       on this resolved boolean; a frozen bridge would hang the caller's await
       and the row would never resolve. On timeout, return false so the row
       cleanly stays hidden (identical to "no coverage"). */
    const r = await withDeadline<{ available?: boolean }>(p.probe({ lat, lng }), 6000, { available: false });
    return r?.available === true;
  } catch {
    return false;
  }
}

/** Present the Look Around sheet. No-op off-native; resolves ok=false when the
 *  scene cannot be shown (e.g. coverage vanished between probe and tap). */
export async function lookAroundOpen(lat: number, lng: number): Promise<boolean> {
  if (!isNativeIOS()) return false;
  try {
    const p = await getLookAroundPlugin();
    /* 2026-07-29 · deadline-guard the present call so a stalled sheet cannot
       leave this await pending. Callers ignore the resolved value, so an early
       false is harmless; the native presentation is unaffected. */
    const r = await withDeadline<{ ok?: boolean }>(p.open({ lat, lng }), 12000, { ok: false });
    return r?.ok === true;
  } catch {
    return false;
  }
}

/* ci-lineage note (b77) · main's mid-file nativePlugin helper does not exist
   in this branch's rival copy of the file, so the driveAudio section below
   carries its own identical copy rather than reaching for a symbol the
   auto-merge never brought over. */
async function nativePlugin<T>(name: string): Promise<T | null> {
  if (!isNativeIOS()) return null;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    return registerPlugin<T>(name) as T;
  } catch {
    return null;
  }
}

// ---- Drive Mode background audio (b77 · founder sign-off 2026-08-04) ------
// wedgeVoice.ts speaks through live speechSynthesis, which iOS silences the
// moment the app backgrounds or the phone locks. The WedgeAudio app-local
// plugin renders each script line to a REAL audio file on device
// (AVSpeechSynthesizer.write -> AVAudioPlayer) under a .playback audio
// session, so a Drive brief keeps reading in Apple Maps and on the lock
// screen, with REAL durations and Now Playing state (nothing fabricated:
// every duration comes from the rendered file, every dock state from the
// live player). Web keeps the speechSynthesis path byte for byte.

export interface DriveAudioState {
  index: number;
  playing: boolean;
  done: boolean;
  /* Real seconds from the rendered line file and the live player position.
     Absent (never invented) while a line is still synthesizing. */
  duration?: number;
  position?: number;
}

interface WedgeAudioPluginShape {
  playScript(o: { lines: string[]; startIndex: number; rate: number; title: string }): Promise<{ ok: boolean }>;
  pause(): Promise<void>;
  resume(): Promise<void>;
  stop(): Promise<void>;
  setRate(o: { rate: number }): Promise<void>;
  skipTo(o: { index: number }): Promise<void>;
  getState(): Promise<DriveAudioState>;
  addListener(eventName: 'driveAudioState', cb: (s: DriveAudioState) => void): Promise<{ remove(): Promise<void> }>;
}

/* The channel object the prototype reads through __WEDGE_FX__.driveAudio.
   `ready` flips true only after a live probe answers, so the prototype can
   branch SYNCHRONOUSLY at mount (an unresolved probe just means the web
   speechSynthesis path runs, which is exactly the old behavior). The probe
   guards the #440 mirror-image failure: JS shipped against a Swift plugin
   that is not in the installed shell must fall back, not go silent. */
export const driveAudio = {
  ready: false,
  async playScript(lines: string[], startIndex: number, rate: number, title: string): Promise<boolean> {
    const P = await nativePlugin<WedgeAudioPluginShape>('WedgeAudio');
    if (!P) return false;
    try { return (await P.playScript({ lines, startIndex, rate, title })).ok === true; } catch { return false; }
  },
  pause(): void { void nativePlugin<WedgeAudioPluginShape>('WedgeAudio').then((P) => P?.pause()).catch(() => undefined); },
  resume(): void { void nativePlugin<WedgeAudioPluginShape>('WedgeAudio').then((P) => P?.resume()).catch(() => undefined); },
  stop(): void { void nativePlugin<WedgeAudioPluginShape>('WedgeAudio').then((P) => P?.stop()).catch(() => undefined); },
  setRate(rate: number): void { void nativePlugin<WedgeAudioPluginShape>('WedgeAudio').then((P) => P?.setRate({ rate })).catch(() => undefined); },
  skipTo(index: number): void { void nativePlugin<WedgeAudioPluginShape>('WedgeAudio').then((P) => P?.skipTo({ index })).catch(() => undefined); },
  async getState(): Promise<DriveAudioState | null> {
    const P = await nativePlugin<WedgeAudioPluginShape>('WedgeAudio');
    if (!P) return null;
    try { return await P.getState(); } catch { return null; }
  },
  /* Subscribe to native queue advances. Returns an unsubscribe fn. Events can
     be dropped while the webview is backgrounded, so callers reconcile with
     getState() on visibilitychange rather than trusting the stream alone. */
  onState(cb: (s: DriveAudioState) => void): () => void {
    let removed = false;
    let handle: { remove(): Promise<void> } | null = null;
    void nativePlugin<WedgeAudioPluginShape>('WedgeAudio')
      .then(async (P) => {
        if (!P || removed) return;
        try { handle = await P.addListener('driveAudioState', cb); } catch { /* absent plugin · fallback path active */ }
        if (removed && handle) void handle.remove();
      })
      .catch(() => undefined);
    return () => { removed = true; if (handle) void handle.remove().catch(() => undefined); };
  },
};

/** Probe once at shell install. getState() on the live plugin resolves;
 *  a registerPlugin proxy for an absent Swift half rejects "not implemented"
 *  and `ready` stays false forever, which IS the honest answer there. */
export function initDriveAudio(): void {
  if (!isNativeIOS()) return;
  void (async () => {
    try {
      const P = await nativePlugin<WedgeAudioPluginShape>('WedgeAudio');
      if (!P) return;
      await P.getState();
      driveAudio.ready = true;
    } catch { /* older shell without WedgeAudio.swift · speechSynthesis path */ }
  })();
}
